// client/src/services/api.ts (converted from .js, M4-TS proof conversion)
//
// The shared axios instance plus the per-feature endpoint namespaces
// (`api.customSummaries.getAll(...)`, …). Before the conversion each namespace
// was bolted on with `api.x = {…}`; TypeScript needs the shape up front, so each
// namespace is now a const and `Object.assign` attaches them to the SAME axios
// instance at the bottom — `api === http`, so the 142 importers,
// `api.get/post/…`, the interceptors and every vi.mock of this module behave
// exactly as before. Namespace methods call `http.*` (the instance) directly.
//
// Parameter types are deliberately loose where the endpoint's shape is not
// modelled yet (`QueryParams`, `JsonBody` = any object). Typed response shapes
// for new code belong in src/types/* and are passed as `http.get<T>()` /
// `api.get<T>()` generics by the caller or by a typed service wrapper
// (see services/lexistar*.ts).
import axios, { type AxiosInstance, type AxiosProgressEvent, type AxiosRequestConfig } from 'axios';
import { installDemoMasking } from '../utils/demoMode';
import { installAuthInterceptors } from './authClient';
import type { MonitorRunJob, MonitorRunJobStart } from '../types/clientPages/routeIntelligence';
import type { LocationSuggestionsResponse } from '../types/loadMatching';
import type { VehicleListResponse } from '../types/clientPages/vehicleRouteTracker';

/** Record/row ids travel as numbers or strings (ticket UIDs, uuids). */
export type Id = string | number;
/** Query-string parameters (axios `params`). Any object — endpoint shapes are not modelled here. */
export type QueryParams = object;
/** JSON request body. Any object (arrays included) — endpoint shapes are not modelled here. */
export type JsonBody = object;

// Create axios instance with default config
const http: AxiosInstance = axios.create({
  baseURL: import.meta.env.REACT_APP_API_URL || 'http://localhost:5002/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // Default 30 second timeout
  withCredentials: true,
});

// Cookie-session auth: withCredentials + CSRF header + shared single-flight
// refresh on 401 (services/authClient.js). Replaces the keycloak-js token flow.
installAuthInterceptors(http);

// Back-compat no-op — auth is cookie-based now, nothing to initialize.
export const initializeAuth = () => {};

// DAQ-7 / ST-00201: Telegram BI and shared-query calls used to carry a hard-coded
// `x-user-id: 123456789` header, so every dashboard user shared one BI identity. The
// server now takes the user from the session cookie; no identity header is sent.

// Demo mode: mask responses, send the real value back for a masked one (utils/demoMode, PLT-12).
installDemoMasking(http);

// Response interceptor: tenant-error tagging
http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 403) {
      const msg = error.response.data?.message || error.response.data?.error || '';
      if (msg.toLowerCase().includes('tenant') || msg.toLowerCase().includes('suspended')) {
        error.tenantError = true;
      }
    }
    return Promise.reject(error);
  }
);

// Authentication endpoints (cookie session)
const auth = {
  me: () => http.get('/auth/me'),
  login: (email: string, password: string) => http.post('/auth/login', { email, password }),
  logout: () => http.post('/auth/logout'),
  forgotPassword: (email: string) => http.post('/auth/forgot-password', { email }),
  setPassword: (token: string, password: string) =>
    http.post('/auth/set-password', { token, password }),
  changePassword: (currentPassword: string, newPassword: string) =>
    http.post('/auth/change-password', { currentPassword, newPassword }),
};

// --- Load Classification API methods commented out ---
/*
const loadClassification = {
  // Process LexiBridgeDataView and populate LoadClassification table
  process: (clearExisting = true) => {
    return http.post('/load-classification/process', { clearExisting });
  },

  // Get load classifications with optional filters
  getAll: (params?: QueryParams) => {
    return http.get('/load-classification', { params });
  },

  // Get statistics about load classifications
  getStats: (params?: QueryParams) => {
    return http.get('/load-classification/stats', { params });
  }
};
*/
// --- End of commented out Load Classification API methods ---

// Amendments V3 API methods
// Bulk amendments write one ticket per transaction on the server, each taking the
// ticket lock and rebuilding its data-view row (ST-00215), so allow time per
// ticket on top of the 2-minute floor. A client timeout mid-run makes users
// retry, which would stack a second version on every ticket (review N14).
const bulkTimeout = (ticketCount: unknown): number =>
  Math.max(120000, (Number(ticketCount) || 0) * 250);

/** The parts of a bulk-amend payload the timeout is sized from. */
interface BulkAmendPayload {
  ticketUIDs?: unknown[];
  amendments?: unknown[];
}

const amendmentsV3 = {
  // Get latest tickets (paginated and filtered)
  getLatestTickets: (params?: QueryParams, config: AxiosRequestConfig = {}) => {
    return http.get('/amendments/v3', { params, timeout: 90000, ...config });
  },

  // Get historical/amended tickets (paginated and filtered)
  getAmendedTickets: (params?: QueryParams, config: AxiosRequestConfig = {}) => {
    return http.get('/amendments/v3/amended', { params, timeout: 90000, ...config });
  },

  // Get ALL amended tickets matching filters for export (no pagination)
  getAmendedTicketsForExport: (params?: QueryParams) => {
    console.log('API V3: Sending export request for amended tickets with filters:', params);
    return http.get('/amendments/v3/amended/export', { params, timeout: 90000 });
  },

  // Get ALL current tickets matching filters for export (no pagination)
  getLatestTicketsForExport: (params?: QueryParams) => {
    console.log('API V3: Sending export request for current tickets with filters:', params);
    return http.get('/amendments/v3/export', { params, timeout: 90000 });
  },

  // Excluded tickets (paginated, same filters as the Current tab). Excluded
  // tickets are not in the data view, so this is the only list that has them (AMD-4).
  getExcludedTickets: (params?: QueryParams, config: AxiosRequestConfig = {}) => {
    return http.get('/amendments/v3/excluded', { params, timeout: 90000, ...config });
  },

  // Get version history for a specific ticket by TicketUID
  getHistory: (ticketUID: Id) => {
    return http.get(`/amendments/v3/history/${ticketUID}`);
  },

  getTimeline: (ticketUID: Id) => {
    return http.get(`/amendments/v3/history/${ticketUID}/timeline`);
  },

  // Perform an amendment on a ticket
  amendTicket: (ticketUID: Id, amendmentData: JsonBody) => {
    console.log(
      `API V3: Sending amendment request for TicketUID ${ticketUID} with data:`,
      JSON.stringify(amendmentData, null, 2)
    );
    return http.post(`/amendments/v3/${ticketUID}`, amendmentData);
  },

  // Perform bulk amendments on multiple tickets (same value for all)
  bulkAmendTickets: (payload: JsonBody & BulkAmendPayload) => {
    console.log(`API V3: Sending bulk amendment request:`, JSON.stringify(payload, null, 2));
    return http.post('/amendments/v3/bulk', payload, {
      timeout: bulkTimeout(payload?.ticketUIDs?.length),
    });
  },

  // Perform per-ticket bulk amendments (each ticket gets its own values)
  bulkAmendIndividual: (payload: JsonBody & BulkAmendPayload) => {
    return http.post('/amendments/v3/bulk-individual', payload, {
      timeout: bulkTimeout(payload?.amendments?.length),
    });
  },

  // Get a single latest ticket by its Ticket No (for validation lookup)
  getTicketByNumber: (ticketNo: Id) => {
    console.log(`API V3: Fetching ticket by number: ${ticketNo}`);
    return http.get(`/amendments/v3/ticket/${ticketNo}`);
  },

  // Find rows matching a search term within the current filtered+sorted set.
  // Returns ordinals + page numbers so the client can jump to the matching row.
  find: (params?: QueryParams, config: AxiosRequestConfig = {}) => {
    return http.get('/amendments/v3/find', { params, timeout: 30000, ...config });
  },

  excludeTicket: (ticketUID: Id, exclude: boolean, amendmentNotes: string) => {
    return http.post(`/amendments/v3/${ticketUID}/exclude`, { exclude, amendmentNotes });
  },
};

// Amendment Approvals API methods
const amendmentApprovals = {
  list: (params?: QueryParams) => http.get('/amendment-approvals', { params }),
  get: (approvalId: Id) => http.get(`/amendment-approvals/${approvalId}`),
  approve: (approvalId: Id, reviewNotes: string) =>
    http.post(`/amendment-approvals/${approvalId}/approve`, { reviewNotes }),
  reject: (approvalId: Id, reviewNotes: string) =>
    http.post(`/amendment-approvals/${approvalId}/reject`, { reviewNotes }),
  withdraw: (approvalId: Id, reviewNotes: string) =>
    http.post(`/amendment-approvals/${approvalId}/withdraw`, { reviewNotes }),
  bulkAction: (approvalIds: Id[], action: string, reviewNotes: string) =>
    http.post('/amendment-approvals/bulk-action', { approvalIds, action, reviewNotes }),
  pendingCount: () => http.get('/amendment-approvals/pending/count'),
  pendingCheck: (ticketUIDs: Id[]) =>
    http.get('/amendment-approvals/pending/check', {
      params: { ticketUIDs: ticketUIDs.join(',') },
    }),
};

// Ticket Attachments API methods
const ticketAttachments = {
  list: (ticketUID: Id) => http.get(`/ticket-attachments/${ticketUID}`),

  upload: (
    ticketUID: Id,
    files: File[],
    onProgress?: (event: AxiosProgressEvent) => void,
    category?: string
  ) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    if (category) formData.append('category', category);
    return http.post(`/ticket-attachments/${ticketUID}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
      onUploadProgress: onProgress,
    });
  },

  update: (ticketUID: Id, attachmentId: Id, data: JsonBody) =>
    http.put(`/ticket-attachments/${ticketUID}/${attachmentId}`, data),

  remove: (ticketUID: Id, attachmentId: Id) =>
    http.delete(`/ticket-attachments/${ticketUID}/${attachmentId}`),

  replaceFile: (ticketUID: Id, attachmentId: Id, file: Blob) => {
    const formData = new FormData();
    formData.append('file', file);
    return http.put(`/ticket-attachments/${ticketUID}/${attachmentId}/file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },

  fileUrl: (storedFilename: string) =>
    `${http.defaults.baseURL}/ticket-attachments/file/${storedFilename}`,
};

// Amendment Analytics API methods
const amendmentAnalytics = {
  summary: (params?: QueryParams) => http.get('/amendment-analytics/summary', { params }),
  byField: (params?: QueryParams) => http.get('/amendment-analytics/by-field', { params }),
  byLocation: (params?: QueryParams) => http.get('/amendment-analytics/by-location', { params }),
  byUser: (params?: QueryParams) => http.get('/amendment-analytics/by-user', { params }),
  trend: (params?: QueryParams) => http.get('/amendment-analytics/trend', { params }),
  timeToAmend: (params?: QueryParams) => http.get('/amendment-analytics/time-to-amend', { params }),
};

// Data API methods (for locations, etc.)
const data = {
  // Get unique dispatch locations from LexiBridgeSource
  // Optional `ticketType` scopes to locations appearing in that transaction type (e.g. 'Dispatch' | 'Receipt')
  getDispatchLocations: (ticketType?: string) => {
    const params = ticketType ? { ticketType } : {};
    return http.get('/data/locations/dispatch', { params });
  },

  // Get unique receipt locations from LexiBridgeSource
  // Optional `dispatchLocation` narrows by routing; optional `ticketType` scopes to locations in that transaction type
  getReceiptLocations: (dispatchLocation?: string, ticketType?: string) => {
    const params: Record<string, string> = {};
    if (dispatchLocation) params.dispatchLocation = dispatchLocation;
    if (ticketType) params.ticketType = ticketType;
    return http.get('/data/locations/receipt', { params });
  },
};

// Signatures API methods
const signatures = {
  // Get signatures for a specific dispatch/receipt location pair
  getSignatures: (dispatchLocation: string, receiptLocation: string) => {
    return http.get('/signatures', { params: { dispatchLocation, receiptLocation } });
  },

  // Create a new signature
  createSignature: (signatureData: JsonBody) => {
    // signatureData = { dispatchLocation, receiptLocation, name, title }
    return http.post('/signatures', signatureData);
  },

  // Get unique signer details (name, title) for autocomplete
  getUniqueSigners: () => {
    return http.get('/signatures/unique-signers');
  },

  // Update a signature
  updateSignature: (id: Id, signatureData: JsonBody) => {
    return http.put(`/signatures/${id}`, signatureData);
  },

  // Delete a signature
  deleteSignature: (id: Id) => {
    return http.delete(`/signatures/${id}`);
  },

  // ==================== GROUPING-BASED SIGNATURES ====================

  // Get signatures for a specific grouping
  getGroupingSignatures: (groupingId: Id) => {
    return http.get(`/signatures/grouping/${groupingId}`);
  },

  // Create a new grouping signature
  createGroupingSignature: (signatureData: JsonBody) => {
    // signatureData = { groupingId, name, title }
    return http.post('/signatures/grouping', signatureData);
  },

  // Delete a grouping signature
  deleteGroupingSignature: (id: Id) => {
    return http.delete(`/signatures/grouping/${id}`);
  },

  // Get unique signers from grouping signatures for autocomplete
  getUniqueGroupingSigners: () => {
    return http.get('/signatures/grouping/unique-signers');
  },

  // Get all unique signers (both location and grouping based)
  getAllUniqueSigners: () => {
    return http.get('/signatures/all/unique-signers');
  },
};

// Logo API methods
const logos = {
  // Get all logos
  getAll: () => {
    return http.get('/logos').then((response) => {
      // Transform the response to include logoPath for PDF generation compatibility
      const logos = (response.data as LogoRow[]).map((logo) => ({
        ...logo,
        logoPath: logo.url ? logo.url.split('/').pop() : null, // Extract filename from URL
      }));
      return { ...response, data: logos };
    });
  },

  // Get a single logo as a base64 data URI ({ dataUri })
  getBase64: (id: Id) => {
    return http.get(`/logos/${id}/base64`);
  },

  // Upload a new logo.
  // formData should contain 'logoName' and 'logoFile'.
  // setAsDefault=true additionally makes it the tenant brand logo (admin-tier).
  upload: (formData: FormData, { setAsDefault = false }: { setAsDefault?: boolean } = {}) => {
    if (setAsDefault && !formData.has('setAsDefault')) {
      formData.append('setAsDefault', 'true');
    }
    return http.post('/logos/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data', // Important for file uploads
      },
    });
  },

  // Make a logo the tenant's brand logo (admin-tier only)
  setDefault: (id: Id) => {
    return http.patch(`/logos/${id}/default`);
  },

  // The current tenant's brand logo:
  // { logoId, name, mimeType, dataUri } or { logoId: null } when none is set
  getBranding: () => {
    return http.get('/logos/branding');
  },

  // Delete a logo by ID
  delete: (id: Id) => {
    return http.delete(`/logos/${id}`);
  },
};

/** A logo row as GET /logos returns it (only the field this module reads is modelled). */
type LogoRow = { url?: string | null } & Record<string, unknown>;

/**
 * Route-summary calls accept two custom flags in the axios config. They steer the
 * query string and timeout here and are then spread into the request config,
 * where axios ignores them (unchanged legacy behaviour).
 */
export type RouteSummaryConfig = AxiosRequestConfig & {
  includeTotals?: boolean;
  forceRefresh?: boolean;
};

// Route Summary API methods
const routeSummary = {
  // Get all groupings (fast - without totals by default)
  getGroupings: (startDate?: string, endDate?: string, config: RouteSummaryConfig = {}) => {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    // PERFORMANCE FIX: Support includeTotals parameter from config
    // Fast path (default): includeTotals=false (5-10x faster, instant route selection)
    // Slow path: includeTotals=true (2-5 seconds, only when explicitly needed)
    if (config.includeTotals !== undefined) {
      params.includeTotals = config.includeTotals ? 'true' : 'false';
    }

    // Use shorter timeout for fast path, longer for slow path
    const timeout = config.includeTotals ? 60000 : 10000;

    return http.get('/route-summary/groupings', {
      params,
      timeout,
      ...config,
    });
  },

  // Get all groupings with totals (slow path - only when needed)
  getGroupingsWithTotals: (
    startDate?: string,
    endDate?: string,
    config: RouteSummaryConfig = {}
  ) => {
    const params: Record<string, string> = { includeTotals: 'true' };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (config.forceRefresh) params.forceRefresh = 'true';
    // Slow path: include totals calculation
    return http.get('/route-summary/groupings', {
      params,
      timeout: 60000, // 60 seconds for totals calculation
      ...config,
    });
  },

  // Check data freshness (lightweight - compares server cache vs last DB write)
  getDataFreshness: (startDate?: string, endDate?: string) => {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return http.get('/route-summary/data-freshness', { params, timeout: 5000 });
  },

  // Get a single grouping by ID
  getGrouping: (id: Id) => {
    return http.get(`/route-summary/groupings/${id}`);
  },

  // Create a new grouping
  createGrouping: (groupingData: JsonBody) => {
    // groupingData = { name, dispatchLocations: [], receiptLocations: [] }
    return http.post('/route-summary/groupings', groupingData);
  },

  // Update a grouping
  updateGrouping: (id: Id, groupingData: JsonBody) => {
    // groupingData = { name, dispatchLocations: [], receiptLocations: [] }
    return http.put(`/route-summary/groupings/${id}`, groupingData);
  },

  // Delete a grouping
  deleteGrouping: (id: Id) => {
    return http.delete(`/route-summary/groupings/${id}`);
  },

  // Generate summary data
  generateSummary: (params?: QueryParams, config: AxiosRequestConfig = {}) => {
    // params = { groupingIds: number[] (or legacy groupingId), startDate, endDate, minWeight }
    // A single-element groupingIds array is treated identically to legacy groupingId.
    // Use 60 second timeout for summary generation as it can be complex
    return http.post('/route-summary/generate', params, {
      timeout: 60000, // 60 seconds
      ...config,
    });
  },

  // Get detailed transactions for a specific date and grouping
  getTransactionDetails: (
    groupingId: Id,
    params?: QueryParams,
    config: AxiosRequestConfig = {}
  ) => {
    // params = { date, type, page, limit }
    // Use 60 second timeout for transaction details as it can involve large datasets
    return http.get(`/route-summary/details/${groupingId}`, {
      params,
      timeout: 60000, // 60 seconds
      ...config,
    });
  },

  // Get the dispatch tickets behind the Brought Forward / Carried Forward rows
  getBfcfDetails: (params?: QueryParams, config: AxiosRequestConfig = {}) => {
    // params = { groupingIds (or legacy groupingId), startDate, endDate, minWeight,
    //            type: 'brought-forward' | 'carried-forward' }
    return http.post('/route-summary/bfcf-details', params, {
      timeout: 60000, // 60 seconds — scans the same window as the summary itself
      ...config,
    });
  },

  // ST-00218 P3: tickets of the route(s) probably keyed with the wrong dispatch / receipt
  // location (shown only, never applied) and locations to add to a route's lists.
  getLocationSuggestions: (
    params: { groupingIds: Id[]; startDate: string; endDate: string },
    config: AxiosRequestConfig = {}
  ) => {
    return http.get<LocationSuggestionsResponse>('/route-summary/location-suggestions', {
      params: {
        groupingIds: params.groupingIds.join(','),
        startDate: params.startDate,
        endDate: params.endDate,
      },
      timeout: 60000, // reads the load-match ledger of the period, like the summary
      ...config,
    });
  },

  // Generate SQL query for a route summary
  generateSQLQuery: (params?: QueryParams, config: AxiosRequestConfig = {}) => {
    // params = { groupingId, startDate, endDate, minWeight }
    return http.post('/route-summary/generate-sql', params, config);
  },

  // Get all transactions for Recon View (paginated, filtered, sorted)
  getReconViewTransactions: (
    groupingId: Id,
    params?: QueryParams,
    config: AxiosRequestConfig = {}
  ) => {
    // params = { startDate, endDate, minWeight, page, limit, filters }
    // Returns all transactions for the selected period, ordered by Reg No and Ticket Timestamp
    return http.get(`/route-summary/recon-view/${groupingId}`, {
      params,
      timeout: 60000, // 60 seconds for large datasets
      ...config,
    });
  },

  // Get unique filter options for Recon View dropdowns
  getReconViewFilterOptions: (
    groupingId: Id,
    params?: QueryParams,
    config: AxiosRequestConfig = {}
  ) => {
    // params = { startDate, endDate, minWeight }
    // Returns unique values for dropdown filters: date, ticketType, ticketNo, vehicleReg
    return http.get(`/route-summary/recon-view/${groupingId}/filter-options`, {
      params,
      timeout: 30000, // 30 seconds
      ...config,
    });
  },

  // Get all transactions for a specific Reg No (for modal display)
  getTransactionsByRegNo: (
    groupingId: Id,
    regNo: string,
    params?: QueryParams,
    config: AxiosRequestConfig = {}
  ) => {
    // params = { startDate, endDate, minWeight }
    // Returns all transactions for the specified Reg No, ordered by Ticket Timestamp
    return http.get(
      `/route-summary/recon-view/${groupingId}/by-reg-no/${encodeURIComponent(regNo)}`,
      {
        params,
        timeout: 30000, // 30 seconds
        ...config,
      }
    );
  },

  // Find rows matching a search term within the current Recon View result set.
  // params = { startDate, endDate, minWeight, filters (JSON string), limit, searchTerm }
  findInReconView: (groupingId: Id, params?: QueryParams, config: AxiosRequestConfig = {}) => {
    return http.get(`/route-summary/recon-view/${groupingId}/find`, {
      params,
      timeout: 30000,
      ...config,
    });
  },

  getExecutiveSummary: (startDate: string, endDate: string, config: AxiosRequestConfig = {}) => {
    return http.get('/route-summary/executive-summary', {
      params: { startDate, endDate },
      timeout: 60000,
      ...config,
    });
  },

  getOverviewAlerts: (config: AxiosRequestConfig = {}) => {
    return http.get('/route-summary/overview-alerts', { timeout: 10000, ...config });
  },

  getRoutePerformance: (startDate: string, endDate: string, config: AxiosRequestConfig = {}) => {
    return http.get('/route-summary/route-performance', {
      params: { startDate, endDate },
      timeout: 60000,
      ...config,
    });
  },

  getLoadMatching: (startDate?: string, endDate?: string, config: AxiosRequestConfig = {}) => {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return http.get('/route-summary/load-matching', { params, timeout: 30000, ...config });
  },
};

// Email API methods
const email = {
  // Get current email settings
  getSettings: () => {
    return http.get('/email/settings');
  },

  // Save email settings
  saveSettings: (smtpConfig: JsonBody, senderInfo: JsonBody) => {
    return http.post('/email/settings', { smtpConfig, senderInfo });
  },

  // Test email connection
  testConnection: () => {
    return http.post('/email/test-connection');
  },

  // Send test email
  sendTestEmail: (testEmail: string) => {
    return http.post('/email/send-test', { testEmail });
  },

  // Send email (general purpose)
  sendEmail: (emailData: JsonBody) => {
    // emailData = { to, cc, bcc, subject, text, html, attachments, type }
    return http.post('/email/send', emailData);
  },

  // Get email logs
  getLogs: (limit = 50, offset = 0) => {
    return http.get('/email/logs', { params: { limit, offset } });
  },

  // Get email statistics
  getStats: () => {
    return http.get('/email/stats');
  },
};

// Error Detection API methods
const errorDetection = {
  // Get all error detection rules
  getRules: () => {
    return http.get('/error-detection/rules');
  },

  // Get current error detection results
  getResults: () => {
    return http.get('/error-detection/results');
  },

  // The latest run's error rows of one rule (at most 5,000; /results sends a sample only)
  getResultDetails: (ruleId: Id) => {
    return http.get(`/error-detection/results/${ruleId}/details`);
  },

  // Run error detection checks
  runCheck: (ruleId: Id | null = null) => {
    return http.post('/error-detection/check', { ruleId });
  },

  // Create new error detection rule
  createRule: (ruleData: JsonBody) => {
    // ruleData = { ruleName, description, sqlQuery, severityThresholds }
    return http.post('/error-detection/rules', ruleData);
  },

  // Update error detection rule
  updateRule: (ruleId: Id, ruleData: JsonBody) => {
    return http.put(`/error-detection/rules/${ruleId}`, ruleData);
  },

  // Delete error detection rule
  deleteRule: (ruleId: Id) => {
    return http.delete(`/error-detection/rules/${ruleId}`);
  },

  // Get notification settings for a rule
  getNotifications: (ruleId: Id) => {
    return http.get(`/error-detection/notifications/${ruleId}`);
  },

  // Create notification setting
  createNotification: (notificationData: JsonBody) => {
    // notificationData = { ruleId, emailAddresses, notifyOnSeverity }
    return http.post('/error-detection/notifications', notificationData);
  },

  // Delete notification setting
  deleteNotification: (notificationId: Id) => {
    return http.delete(`/error-detection/notifications/${notificationId}`);
  },

  // Get error detection history for a rule
  getHistory: (ruleId: Id, days = 7) => {
    return http.get(`/error-detection/history/${ruleId}`, { params: { days } });
  },

  // Daily rollups (streak/alert data) for a rule, last N days
  getDailyResults: (ruleId: Id, days = 30) => {
    return http.get(`/error-detection/daily-results/${ruleId}`, { params: { days } });
  },

  // Recent alert log (tenant-wide; pass ruleId to scope to one rule)
  getAlerts: ({ ruleId = null, limit = 50 }: { ruleId?: Id | null; limit?: number } = {}) => {
    return http.get('/error-detection/alerts', { params: { ruleId, limit } });
  },

  // Manual run that mirrors the scheduler's pipeline (streak + alert)
  runRule: (ruleId: Id) => {
    return http.post(`/error-detection/run/${ruleId}`);
  },

  // Get error detection system settings
  getSettings: () => {
    return http.get('/error-detection/settings');
  },

  // Update error detection system settings
  updateSettings: (settings: JsonBody) => {
    return http.put('/error-detection/settings', settings);
  },

  // Export error detection results
  export: {
    csv: (ruleId: Id) => http.post('/error-detection/export/csv', { ruleId }),
    excel: (ruleId: Id) => http.post('/error-detection/export/excel', { ruleId }),
    pdf: (ruleId: Id) => http.post('/error-detection/export/pdf', { ruleId }),
  },
};

// Route Intelligence API methods
const routeIntelligence = {
  getDashboard: () => http.get('/route-intelligence/dashboard'),
  getMonitors: () => http.get('/route-intelligence/monitors'),
  createMonitor: (data: JsonBody) => http.post('/route-intelligence/monitors', data),
  updateMonitor: (id: Id, data: JsonBody) => http.put(`/route-intelligence/monitors/${id}`, data),
  deleteMonitor: (id: Id) => http.delete(`/route-intelligence/monitors/${id}`),
  getResults: (monitorId: Id, days: number) =>
    http.get(`/route-intelligence/results/${monitorId}`, { params: { days } }),
  getResultSummary: (monitorId: Id) => http.get(`/route-intelligence/results/${monitorId}/summary`),
  // ST-00229 (ANL-15): a run is a background job — 202 { jobId }, then poll getRunJob.
  runMonitor: (monitorId: Id) =>
    http.post<MonitorRunJobStart>(`/route-intelligence/run/${monitorId}`),
  runAll: () => http.post<MonitorRunJobStart>('/route-intelligence/run-all'),
  getRunJob: (jobId: string) => http.get<MonitorRunJob>(`/route-intelligence/run-jobs/${jobId}`),
  acknowledge: (data: JsonBody) => http.post('/route-intelligence/acknowledge', data),
  getAcknowledgments: (resultId: Id) => http.get(`/route-intelligence/acknowledgments/${resultId}`),
  getMyAcknowledgments: (monitorId: Id) =>
    http.get(`/route-intelligence/my-acknowledgments/${monitorId}`),
  getAlertLog: (monitorId: Id) => http.get(`/route-intelligence/alert-log/${monitorId}`),
};

// Preload Evaluation API methods
const preloadEvaluation = {
  getDashboard: () => http.get('/preload-evaluation/dashboard'),
  getMonitors: () => http.get('/preload-evaluation/monitors'),
  createMonitor: (data: JsonBody) => http.post('/preload-evaluation/monitors', data),
  updateMonitor: (id: Id, data: JsonBody) => http.put(`/preload-evaluation/monitors/${id}`, data),
  deleteMonitor: (id: Id) => http.delete(`/preload-evaluation/monitors/${id}`),
  getResults: (monitorId: Id, days: number) =>
    http.get(`/preload-evaluation/results/${monitorId}`, { params: { days } }),
  getResultSummary: (monitorId: Id) => http.get(`/preload-evaluation/results/${monitorId}/summary`),
  getTickets: (resultId: Id) => http.get(`/preload-evaluation/tickets/${resultId}`),
  getMonitorTickets: (monitorId: Id, days: number) =>
    http.get(`/preload-evaluation/tickets/monitor/${monitorId}`, { params: { days } }),
  // ST-00229 (ANL-15): a run is a background job — 202 { jobId }, then poll getRunJob.
  runMonitor: (monitorId: Id) =>
    http.post<MonitorRunJobStart>(`/preload-evaluation/run/${monitorId}`),
  runAll: () => http.post<MonitorRunJobStart>('/preload-evaluation/run-all'),
  getRunJob: (jobId: string) => http.get<MonitorRunJob>(`/preload-evaluation/run-jobs/${jobId}`),
  acknowledge: (data: JsonBody) => http.post('/preload-evaluation/acknowledge', data),
  getAcknowledgments: (resultId: Id) => http.get(`/preload-evaluation/acknowledgments/${resultId}`),
  getMyAcknowledgments: (monitorId: Id) =>
    http.get(`/preload-evaluation/my-acknowledgments/${monitorId}`),
  getAlertLog: (monitorId: Id) => http.get(`/preload-evaluation/alert-log/${monitorId}`),
  testEmail: (monitorId: Id) => http.post(`/preload-evaluation/test-email/${monitorId}`),
};

// Receipt Reconciliation API methods
const receiptRecon = {
  getDashboard: () => http.get('/receipt-recon/dashboard'),
  getMonitors: () => http.get('/receipt-recon/monitors'),
  createMonitor: (data: JsonBody) => http.post('/receipt-recon/monitors', data),
  updateMonitor: (id: Id, data: JsonBody) => http.put(`/receipt-recon/monitors/${id}`, data),
  deleteMonitor: (id: Id) => http.delete(`/receipt-recon/monitors/${id}`),
  getResults: (monitorId: Id, days: number) =>
    http.get(`/receipt-recon/results/${monitorId}`, { params: { days } }),
  getResultSummary: (monitorId: Id) => http.get(`/receipt-recon/results/${monitorId}/summary`),
  getTickets: (resultId: Id) => http.get(`/receipt-recon/tickets/${resultId}`),
  getMonitorTickets: (monitorId: Id, days: number) =>
    http.get(`/receipt-recon/tickets/monitor/${monitorId}`, { params: { days } }),
  // ST-00229 (ANL-15): a run is a background job — 202 { jobId }, then poll getRunJob.
  runMonitor: (monitorId: Id) => http.post<MonitorRunJobStart>(`/receipt-recon/run/${monitorId}`),
  runAll: () => http.post<MonitorRunJobStart>('/receipt-recon/run-all'),
  getRunJob: (jobId: string) => http.get<MonitorRunJob>(`/receipt-recon/run-jobs/${jobId}`),
  acknowledge: (data: JsonBody) => http.post('/receipt-recon/acknowledge', data),
  getAcknowledgments: (resultId: Id) => http.get(`/receipt-recon/acknowledgments/${resultId}`),
  getMyAcknowledgments: (monitorId: Id) =>
    http.get(`/receipt-recon/my-acknowledgments/${monitorId}`),
  getAlertLog: (monitorId: Id) => http.get(`/receipt-recon/alert-log/${monitorId}`),
  testEmail: (monitorId: Id) => http.post(`/receipt-recon/test-email/${monitorId}`),
};

// Email Reports API methods
const emailReports = {
  // Get all email reports
  getAll: () => {
    return http.get('/email-reports');
  },

  // Get specific email report
  get: (id: Id) => {
    return http.get(`/email-reports/${id}`);
  },

  // Create new email report
  create: (reportData: JsonBody) => {
    // reportData = { reportName, description, dateRangeType, surveyMonthStartDay, sharePointLinks, recipients, routeGroupings, isActive }
    return http.post('/email-reports', reportData);
  },

  // Update email report
  update: (id: Id, reportData: JsonBody) => {
    return http.put(`/email-reports/${id}`, reportData);
  },

  // Switch a report on or off (ST-00238): only the report's is_active changes
  setActive: (id: Id, active: boolean) => {
    return http.patch(`/email-reports/${id}/active`, { active });
  },

  // Delete email report
  delete: (id: Id) => {
    return http.delete(`/email-reports/${id}`);
  },

  // Send email report manually
  send: (id: Id) => {
    return http.post(`/email-reports/${id}/send`);
  },

  // Preview email content
  preview: (id: Id) => {
    return http.get(`/email-reports/${id}/preview`);
  },

  // Get execution history
  getExecutions: (id: Id, page = 1, limit = 20) => {
    return http.get(`/email-reports/${id}/executions`, {
      params: { page, limit },
    });
  },

  // Get specific execution details
  getExecution: (id: Id, executionId: Id) => {
    return http.get(`/email-reports/${id}/executions/${executionId}`);
  },

  // Schedule management
  createSchedule: (id: Id, scheduleData: JsonBody) => {
    // scheduleData = { scheduleType, scheduleTime, timezone, isActive }
    return http.post(`/email-reports/${id}/schedule`, scheduleData);
  },

  // Update schedule
  updateSchedule: (id: Id, scheduleData: JsonBody) => {
    return http.post(`/email-reports/${id}/schedule`, scheduleData);
  },

  // Delete schedule
  deleteSchedule: (id: Id) => {
    return http.delete(`/email-reports/${id}/schedule`);
  },

  // Template management
  getTemplates: () => {
    return http.get('/email-reports/templates');
  },

  // Create custom template
  createTemplate: (templateData: JsonBody) => {
    // templateData = { templateName, templateType, subjectTemplate, htmlTemplate, isDefault }
    return http.post('/email-reports/templates', templateData);
  },

  // Update template
  updateTemplate: (id: Id, templateData: JsonBody) => {
    return http.put(`/email-reports/templates/${id}`, templateData);
  },

  // Delete template
  deleteTemplate: (id: Id) => {
    return http.delete(`/email-reports/templates/${id}`);
  },

  // Get available route groupings
  getRouteGroupings: () => {
    return http.get('/email-reports/route-groupings');
  },
};

// Shared Queries API methods (used by advanced email reports, independent of Telegram BI)
const sharedQueries = {
  // Get all queries with pagination and filtering
  getAll: (params: QueryParams = {}) => {
    return http.get('/shared/queries', { params });
  },

  // Get specific query by ID
  get: (id: Id) => {
    return http.get(`/shared/queries/${id}`);
  },

  // Create new query
  create: (queryData: JsonBody) => {
    return http.post('/shared/queries', queryData);
  },

  // Update query
  update: (id: Id, queryData: JsonBody) => {
    return http.put(`/shared/queries/${id}`, queryData);
  },

  // Delete query
  delete: (id: Id) => {
    return http.delete(`/shared/queries/${id}`);
  },

  // Execute query (for preview/testing)
  execute: (id: Id, parameters: QueryParams = {}) => {
    return http.post(`/shared/queries/${id}/execute`, { parameters });
  },

  // Validate SQL query
  validate: (sqlQuery: string, dataSourceId: Id) => {
    return http.post('/shared/queries/validate', { sqlQuery, dataSourceId });
  },

  // Get available data sources
  getDataSources: () => {
    return http.get('/shared/queries/data-sources');
  },

  // Get query execution history
  getHistory: (id: Id, params: QueryParams = {}) => {
    return http.get(`/shared/queries/${id}/history`, { params });
  },
};

// Location Analytics API methods
const locationAnalytics = {
  // Get locations by type (Dispatch or Receipt) with total weights
  getLocationsByType: (
    type: string,
    startDate: string,
    endDate: string,
    config: AxiosRequestConfig = {}
  ) => {
    // type: 'Dispatch' or 'Receipt'
    // Returns: [{ location, totalWeight, ticketCount }]
    return http.get(`/location-analytics/locations/${type}`, {
      params: { startDate, endDate },
      timeout: 30000, // 30 seconds
      ...config,
    });
  },

  // Get related locations when primary location is selected
  getRelatedLocations: (
    primaryLocation: string,
    type: string,
    startDate: string,
    endDate: string,
    config: AxiosRequestConfig = {}
  ) => {
    // If type='Dispatch', returns Receipt locations for this dispatch
    // If type='Receipt', returns Dispatch locations for this receipt
    // Returns: [{ location, nettWeight, ticketCount }]
    return http.get('/location-analytics/related-locations', {
      params: { primaryLocation, type, startDate, endDate },
      timeout: 30000, // 30 seconds
      ...config,
    });
  },

  // Get summary statistics for the selected date range and type
  getSummary: (
    type: string,
    startDate: string,
    endDate: string,
    config: AxiosRequestConfig = {}
  ) => {
    // Returns: { totalTickets, totalWeight, uniqueLocations, averageWeight }
    return http.get('/location-analytics/summary', {
      params: { type, startDate, endDate },
      timeout: 30000, // 30 seconds
      ...config,
    });
  },

  // Drill explorer: KPIs + daily trend + drillable dimension breakdowns for a scope.
  // params: { type, startDate, endDate, location?, product?, partner? }
  // Returns: { scope, kpis, trend: [{date, weight, tickets}], dimensions: { location?, product?, partner? } }
  getDrill: (params?: QueryParams, config: AxiosRequestConfig = {}) => {
    return http.get('/location-analytics/drill', {
      params,
      timeout: 30000,
      ...config,
    });
  },

  // Paginated ticket-level detail for a drill scope (deepest drill level).
  // params: { type, startDate, endDate, location?, product?, partner?, page?, limit? }
  // Returns: { page, limit, total, tickets: [...] }
  getDrillTickets: (params?: QueryParams, config: AxiosRequestConfig = {}) => {
    return http.get('/location-analytics/drill/tickets', {
      params,
      timeout: 30000,
      ...config,
    });
  },
};

// Vehicle Route Tracker API methods
const vehicleTracker = {
  searchVehicles: (
    query: string,
    startDate?: string,
    endDate?: string,
    config: AxiosRequestConfig = {}
  ) => {
    const params: Record<string, string> = { q: query };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return http.get('/vehicle-tracker/search', {
      params,
      timeout: 30000,
      ...config,
    });
  },

  /** ST-00242: the registrations seen in the period, for the page's registration picker. */
  listVehicles: (startDate: string, endDate: string, config: AxiosRequestConfig = {}) =>
    http.get<VehicleListResponse>('/vehicle-tracker/vehicles', {
      params: { startDate, endDate },
      timeout: 30000,
      ...config,
    }),

  getVehicleStats: (
    regNo: string,
    startDate: string,
    endDate: string,
    config: AxiosRequestConfig = {}
  ) => {
    return http.get(`/vehicle-tracker/${encodeURIComponent(regNo)}/stats`, {
      params: { startDate, endDate },
      timeout: 30000,
      ...config,
    });
  },

  getVehicleTrips: (regNo: string, params?: QueryParams, config: AxiosRequestConfig = {}) => {
    return http.get(`/vehicle-tracker/${encodeURIComponent(regNo)}/trips`, {
      params,
      timeout: 60000,
      ...config,
    });
  },

  getRouteNetwork: (
    regNo: string,
    startDate: string,
    endDate: string,
    config: AxiosRequestConfig = {}
  ) => {
    return http.get(`/vehicle-tracker/${encodeURIComponent(regNo)}/route-network`, {
      params: { startDate, endDate },
      timeout: 30000,
      ...config,
    });
  },

  getDailyActivity: (
    regNo: string,
    startDate: string,
    endDate: string,
    config: AxiosRequestConfig = {}
  ) => {
    return http.get(`/vehicle-tracker/${encodeURIComponent(regNo)}/daily-activity`, {
      params: { startDate, endDate },
      timeout: 30000,
      ...config,
    });
  },
};

// Transaction Allocation API methods
const transactionAllocation = {
  // Get transactions with allocation suggestions
  getTransactionsWithSuggestions: (params?: QueryParams, config: AxiosRequestConfig = {}) => {
    // params = { groupingId, startDate, endDate, timingHours }
    // Returns: { transactions: [...] }
    return http.get('/transaction-allocation/suggestions', {
      params,
      timeout: 60000, // 60 seconds for complex allocation logic
      ...config,
    });
  },

  // Apply bulk allocations using amendment system
  applyAllocations: (payload: JsonBody, config: AxiosRequestConfig = {}) => {
    // payload = { allocations: [...], amendmentNotes }
    // allocations = [{ receiptTicketUID, suggestedTicketNo, dispatchTicketUID }]
    return http.post('/transaction-allocation/apply', payload, {
      timeout: 60000, // 60 seconds for bulk operations
      ...config,
    });
  },

  // Get detailed transaction data by TicketUID
  getTransactionDetails: (ticketUID: Id, config: AxiosRequestConfig = {}) => {
    // Returns detailed transaction data for the modal
    return http.get(`/transaction-allocation/transaction/${ticketUID}`, {
      timeout: 30000, // 30 seconds
      ...config,
    });
  },
};

// Database API methods
const database = {
  // Get current index status
  getIndexStatus: () => {
    return http.get('/database/indexes/status');
  },

  // (Rebuild / verify were removed with their endpoints, ST-00222 IAM-19: indexes are
  // created by the migrations on every deploy.)

  // Get database health information
  getHealth: () => {
    return http.get('/database/health');
  },
};

// Custom Summaries API methods
const customSummaries = {
  // Get all summaries with filtering and pagination
  getAll: (params: QueryParams = {}, config: AxiosRequestConfig = {}) => {
    return http.get('/custom-summaries', { params, ...config });
  },

  // Get specific summary by ID
  get: (id: Id) => {
    return http.get(`/custom-summaries/${id}`);
  },

  // Create new summary
  create: (summaryData: JsonBody) => {
    return http.post('/custom-summaries', summaryData);
  },

  // Update summary
  update: (id: Id, summaryData: JsonBody) => {
    return http.put(`/custom-summaries/${id}`, summaryData);
  },

  // Delete summary
  delete: (id: Id) => {
    return http.delete(`/custom-summaries/${id}`);
  },

  // Execute summary
  execute: (id: Id, parameters: QueryParams = {}) => {
    return http.post(`/custom-summaries/${id}/execute`, { parameters });
  },

  // Duplicate summary
  duplicate: (id: Id, title: string) => {
    return http.post(`/custom-summaries/${id}/duplicate`, { title });
  },

  // Get execution history
  getHistory: (id: Id, params: QueryParams = {}) => {
    return http.get(`/custom-summaries/${id}/history`, { params });
  },

  // Templates API
  templates: {
    // Get all templates
    getAll: (params: QueryParams = {}) => {
      return http.get('/custom-summaries/templates', { params });
    },

    // Get specific template
    get: (id: Id) => {
      return http.get(`/custom-summaries/templates/${id}`);
    },

    // Create template
    create: (templateData: JsonBody) => {
      return http.post('/custom-summaries/templates', templateData);
    },

    // Update template
    update: (id: Id, templateData: JsonBody) => {
      return http.put(`/custom-summaries/templates/${id}`, templateData);
    },

    // Delete template
    delete: (id: Id) => {
      return http.delete(`/custom-summaries/templates/${id}`);
    },

    // Create summary from template
    createFromTemplate: (templateId: Id, summaryData: JsonBody) => {
      return http.post(`/custom-summaries/templates/${templateId}/create-summary`, summaryData);
    },
  },

  // Scheduling API
  schedules: {
    // Get schedules for a summary
    getAll: (summaryId: Id, params: QueryParams = {}) => {
      return http.get(`/custom-summaries/${summaryId}/schedules`, { params });
    },

    // Create schedule
    create: (summaryId: Id, scheduleData: JsonBody) => {
      return http.post(`/custom-summaries/${summaryId}/schedules`, scheduleData);
    },

    // Update schedule
    update: (summaryId: Id, scheduleId: Id, scheduleData: JsonBody) => {
      return http.put(`/custom-summaries/${summaryId}/schedules/${scheduleId}`, scheduleData);
    },

    // Delete schedule
    delete: (summaryId: Id, scheduleId: Id) => {
      return http.delete(`/custom-summaries/${summaryId}/schedules/${scheduleId}`);
    },

    // Get schedule execution history
    getExecutions: (summaryId: Id, scheduleId: Id, params: QueryParams = {}) => {
      return http.get(`/custom-summaries/${summaryId}/schedules/${scheduleId}/executions`, {
        params,
      });
    },
  },

  // Sharing API
  shares: {
    // Get shares for a summary
    getAll: (summaryId: Id) => {
      return http.get(`/custom-summaries/${summaryId}/shares`);
    },

    // Create share
    create: (summaryId: Id, shareData: JsonBody) => {
      return http.post(`/custom-summaries/${summaryId}/shares`, shareData);
    },

    // Update share
    update: (summaryId: Id, shareId: Id, shareData: JsonBody) => {
      return http.put(`/custom-summaries/${summaryId}/shares/${shareId}`, shareData);
    },

    // Delete share
    delete: (summaryId: Id, shareId: Id) => {
      return http.delete(`/custom-summaries/${summaryId}/shares/${shareId}`);
    },
  },

  // Export API
  export: {
    // Export summary results to CSV
    csv: (id: Id, parameters: QueryParams = {}) => {
      return http.post(`/custom-summaries/${id}/export/csv`, { parameters });
    },

    // Export summary results to Excel
    excel: (id: Id, parameters: QueryParams = {}) => {
      return http.post(`/custom-summaries/${id}/export/excel`, { parameters });
    },

    // Export summary results to PDF
    pdf: (id: Id, parameters: QueryParams = {}) => {
      return http.post(`/custom-summaries/${id}/export/pdf`, { parameters });
    },
  },

  // Query Builder API
  queryBuilder: {
    // Get database schema
    getSchema: () => {
      return http.get('/custom-summaries/query-builder/schema');
    },

    // Validate query
    validateQuery: (query: string, parameters: QueryParams = {}) => {
      return http.post('/custom-summaries/query-builder/validate', { query, parameters });
    },

    // Test query execution
    testQuery: (query: string, parameters: QueryParams = {}) => {
      return http.post('/custom-summaries/query-builder/test', { query, parameters });
    },

    // Get sample queries
    getSamples: () => {
      return http.get('/custom-summaries/query-builder/samples');
    },
  },

  // Analytics API
  analytics: {
    // Get summary usage statistics
    getUsageStats: (params: QueryParams = {}) => {
      return http.get('/custom-summaries/analytics/usage', { params });
    },

    // Get performance metrics
    getPerformanceMetrics: (params: QueryParams = {}) => {
      return http.get('/custom-summaries/analytics/performance', { params });
    },

    // Get popular summaries
    getPopular: (params: QueryParams = {}) => {
      return http.get('/custom-summaries/analytics/popular', { params });
    },
  },

  // Cache management
  cache: {
    // Clear cache for a summary
    clear: (id: Id) => {
      return http.delete(`/custom-summaries/${id}/cache`);
    },

    // Get cache statistics
    getStats: () => {
      return http.get('/custom-summaries/cache/stats');
    },
  },
};

// Telegram BI API methods
const telegramBI = {
  // Bot Management
  bot: {
    // Get bot status and configuration
    getStatus: () => {
      return http.get('/telegram/bot/info');
    },

    // Get bot statistics
    getStats: (params: QueryParams = {}) => {
      return http.get('/telegram/bot/stats', { params });
    },

    // Get registered chats
    getChats: (params: QueryParams = {}) => {
      // Add cache busting and better error handling
      const requestParams = {
        ...params,
        _t: Date.now(), // Cache buster
      };

      return http.get('/telegram/bot/chats', {
        params: requestParams,
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
    },

    // Set webhook
    setWebhook: (webhookData: JsonBody) => {
      return http.post('/telegram/bot/webhook/set', webhookData);
    },

    // Delete webhook
    deleteWebhook: () => {
      return http.delete('/telegram/bot/webhook');
    },

    // Send test message
    sendTestMessage: (chatId: Id, message: string) => {
      return http.post('/telegram/bot/message', { chatId, text: message });
    },

    // Get bot commands
    getCommands: () => {
      return http.get('/telegram/bot/commands');
    },
  },

  // Query Management
  queries: {
    // Get all queries with pagination and filtering
    getAll: (params: QueryParams = {}, config: AxiosRequestConfig = {}) => {
      return http.get('/telegram/queries', { params, ...config });
    },

    // Get specific query by ID
    get: (id: Id) => {
      return http.get(`/telegram/queries/${id}`);
    },

    // Create new query
    create: (queryData: JsonBody) => {
      return http.post('/telegram/queries', queryData);
    },

    // Update query
    update: (id: Id, queryData: JsonBody) => {
      return http.put(`/telegram/queries/${id}`, queryData);
    },

    // Delete query
    delete: (id: Id) => {
      return http.delete(`/telegram/queries/${id}`);
    },

    // Execute query
    execute: (id: Id, parameters: QueryParams = {}) => {
      return http.post(`/telegram/queries/${id}/execute`, { parameters });
    },

    // Validate SQL query
    validate: (sqlQuery: string, dataSourceId: Id) => {
      return http.post('/telegram/queries/validate', { sqlQuery, dataSourceId });
    },

    // Get query execution history
    getHistory: (id: Id, params: QueryParams = {}) => {
      return http.get(`/telegram/queries/${id}/history`, { params });
    },

    // Duplicate query
    duplicate: (id: Id, name: string) => {
      return http.post(`/telegram/queries/${id}/duplicate`, { name });
    },
  },

  // Template Management
  templates: {
    // Get all templates with pagination and filtering
    getAll: (params: QueryParams = {}, config: AxiosRequestConfig = {}) => {
      return http.get('/telegram/templates', { params, ...config });
    },

    // Get specific template by ID
    get: (id: Id) => {
      return http.get(`/telegram/templates/${id}`);
    },

    // Create new template
    create: (templateData: JsonBody) => {
      return http.post('/telegram/templates', templateData);
    },

    // Update template
    update: (id: Id, templateData: JsonBody) => {
      return http.put(`/telegram/templates/${id}`, templateData);
    },

    // Delete template
    delete: (id: Id) => {
      return http.delete(`/telegram/templates/${id}`);
    },

    // Validate template
    validate: (templateContent: string, sampleData: JsonBody = {}) => {
      return http.post('/telegram/templates/validate', { templateContent, sampleData });
    },

    // Preview template with sample data
    preview: (id: Id, previewData: JsonBody = {}) => {
      // previewData should contain { queryResults: [], variables: {} }
      return http.post(`/telegram/templates/${id}/preview`, previewData);
    },

    // Get template variables
    getVariables: (id: Id) => {
      return http.get(`/telegram/templates/${id}/variables`);
    },

    // Duplicate template
    duplicate: (id: Id, name: string) => {
      return http.post(`/telegram/templates/${id}/duplicate`, { name });
    },

    // Get available queries for template references
    getAvailableQueries: () => {
      return http.get('/telegram/templates/queries/available');
    },

    // Validate query references in template content
    validateQueryReferences: (templateContent: string) => {
      return http.post('/telegram/templates/validate-query-references', { templateContent });
    },

    // Preview template with live query execution
    previewWithQueries: (templateContent: string, templateType = 'text') => {
      return http.post('/telegram/templates/preview-with-queries', {
        templateContent,
        templateType,
      });
    },
  },

  // Schedule Management
  schedules: {
    // Get all schedules with pagination and filtering
    getAll: (params: QueryParams = {}, config: AxiosRequestConfig = {}) => {
      return http.get('/telegram/schedules', { params, ...config });
    },

    // Get specific schedule by ID
    get: (id: Id) => {
      return http.get(`/telegram/schedules/${id}`);
    },

    // Create new schedule
    create: (scheduleData: JsonBody) => {
      return http.post('/telegram/schedules', scheduleData);
    },

    // Update schedule
    update: (id: Id, scheduleData: JsonBody) => {
      return http.put(`/telegram/schedules/${id}`, scheduleData);
    },

    // Delete schedule
    delete: (id: Id) => {
      return http.delete(`/telegram/schedules/${id}`);
    },

    // Execute schedule manually
    execute: (id: Id) => {
      return http.post(`/telegram/schedules/${id}/execute`);
    },

    // Get schedule execution history
    getHistory: (id: Id, params: QueryParams = {}) => {
      return http.get(`/telegram/schedules/${id}/executions`, { params });
    },

    // Pause/resume schedule
    toggleStatus: (id: Id, isActive: boolean) => {
      return http.patch(`/telegram/schedules/${id}/status`, { isActive });
    },
  },

  // Alerts
  alerts: {
    getRules: (params: QueryParams = {}) => {
      return http.get('/telegram/alerts/rules', { params });
    },
    getRule: (id: Id) => {
      return http.get(`/telegram/alerts/rules/${id}`);
    },
    createRule: (data: JsonBody) => {
      return http.post('/telegram/alerts/rules', data);
    },
    updateRule: (id: Id, data: JsonBody) => {
      return http.put(`/telegram/alerts/rules/${id}`, data);
    },
    deleteRule: (id: Id) => {
      return http.delete(`/telegram/alerts/rules/${id}`);
    },
    testRule: (id: Id) => {
      return http.post(`/telegram/alerts/rules/${id}/test`);
    },
    acknowledge: (id: Id) => {
      return http.post(`/telegram/alerts/${id}/acknowledge`);
    },
    silence: (id: Id, duration = 60) => {
      return http.post(`/telegram/alerts/${id}/silence`, { duration });
    },
    getHistory: (params: QueryParams = {}) => {
      return http.get('/telegram/alerts/history', { params });
    },
  },

  // Analytics and Monitoring
  analytics: {
    // Get dashboard analytics
    getDashboard: (params: QueryParams = {}) => {
      return http.get('/telegram/analytics/dashboard', { params });
    },

    // Get system-wide analytics
    getSystem: (params: QueryParams = {}) => {
      return http.get('/telegram/analytics/system', { params });
    },

    // Get health status
    getHealth: () => {
      return http.get('/telegram/analytics/health');
    },

    // Get query performance metrics
    getQueryPerformance: (params: QueryParams = {}) => {
      return http.get('/telegram/analytics/query-performance', { params });
    },

    // Get message delivery statistics
    getDeliveryStats: (params: QueryParams = {}) => {
      return http.get('/telegram/analytics/delivery-stats', { params });
    },

    // Get user activity metrics
    getUserActivity: (params: QueryParams = {}) => {
      return http.get('/telegram/analytics/user-activity', { params });
    },

    // Get recent activity feed
    getActivity: (params: QueryParams = {}) => {
      return http.get('/telegram/analytics/activity', { params });
    },
  },

  // Data Sources Management
  dataSources: {
    // Get all data sources
    getAll: () => {
      return http.get('/telegram/data-sources');
    },

    // Test data source connection
    testConnection: (dataSourceId: Id) => {
      return http.post(`/telegram/data-sources/${dataSourceId}/test`);
    },
  },

  // User Management
  users: {
    // Get all Telegram users/chats
    getAll: (params: QueryParams = {}) => {
      return http.get('/telegram/users', { params });
    },

    // Get user permissions
    getPermissions: (chatId: Id) => {
      return http.get(`/telegram/users/${chatId}/permissions`);
    },

    // Update user permissions
    updatePermissions: (chatId: Id, permissions: JsonBody) => {
      return http.put(`/telegram/users/${chatId}/permissions`, permissions);
    },
  },

  // System Configuration
  config: {
    // Get system configuration
    get: () => {
      return http.get('/telegram/config');
    },

    // Update system configuration
    update: (configData: JsonBody) => {
      return http.put('/telegram/config', configData);
    },

    // Get available template helpers
    getTemplateHelpers: () => {
      return http.get('/telegram/config/template-helpers');
    },
  },
};

// Tenant Management API methods
const tenants = {
  getCurrent: () => {
    return http.get('/tenants/current');
  },
  updateCurrent: (data: JsonBody) => {
    return http.put('/tenants/current', data);
  },
  getUsage: () => {
    return http.get('/tenants/current/usage');
  },
  getAll: () => {
    return http.get('/tenants/current/all');
  },
  getAllDetails: () => {
    return http.get('/tenants/current/all/details');
  },
  create: (data: JsonBody) => {
    return http.post('/tenants/current/all', data);
  },
  update: (tenantId: Id, data: JsonBody) => {
    return http.put(`/tenants/current/all/${tenantId}`, data);
  },
  suspend: (tenantId: Id, isSuspended: boolean) => {
    return http.patch(`/tenants/current/all/${tenantId}/suspend`, { isSuspended });
  },
};

// Location Ownership API methods
const locationOwnership = {
  getAll: () => {
    return http.get('/location-ownership');
  },
  getUnassigned: () => {
    return http.get('/location-ownership/unassigned');
  },
  getHealth: () => {
    return http.get('/location-ownership/health');
  },
  getByTenant: (tenantId: Id) => {
    return http.get(`/location-ownership/tenant/${tenantId}`);
  },
  create: (data: JsonBody) => {
    return http.post('/location-ownership', data);
  },
  bulkCreate: (assignments: JsonBody) => {
    return http.post('/location-ownership/bulk', { assignments });
  },
  update: (id: Id, data: JsonBody) => {
    return http.put(`/location-ownership/${id}`, data);
  },
  delete: (id: Id) => {
    return http.delete(`/location-ownership/${id}`);
  },
};

// User Management API methods
const users = {
  getAll: (params: QueryParams = {}) => {
    return http.get('/users', { params });
  },
  get: (userId: Id) => {
    return http.get(`/users/${userId}`);
  },
  create: (userData: JsonBody) => {
    return http.post('/users', userData);
  },
  updateStatus: (userId: Id, isActive: boolean) => {
    return http.patch(`/users/${userId}/status`, { isActive });
  },
  assignRoles: (userId: Id, roleIds: Id[]) => {
    return http.post(`/users/${userId}/roles`, { roleIds });
  },
  delete: (userId: Id) => {
    return http.delete(`/users/${userId}`);
  },
  resetPassword: (userId: Id, newPassword: string) => {
    return http.patch(`/users/${userId}/password`, { newPassword });
  },
  getTenants: (userId: Id) => {
    return http.get(`/users/${userId}/tenants`);
  },
  addToTenant: (userId: Id, tenantId: Id) => {
    return http.post(`/users/${userId}/tenants`, { tenantId });
  },
  removeFromTenant: (userId: Id, tenantId: Id) => {
    return http.delete(`/users/${userId}/tenants/${tenantId}`);
  },
  getMyPermissions: () => {
    return http.get('/users/me/permissions');
  },
  // Admin-only: toggle a user's OData API access (mirrors updateStatus).
  setApiAccess: (userId: Id, enabled: boolean) => {
    return http.patch(`/users/${userId}/api-access`, { enabled });
  },
  // Admin-only: lift a sign-in lock (too many failed attempts).
  unlock: (userId: Id) => {
    return http.post(`/users/${userId}/unlock`);
  },
};

// Personal OData API Keys
// Self-service: users generate/revoke their own keys (own api_access_enabled required).
// Admin: list/revoke any user's keys.
const odataKeys = {
  list: () => http.get('/odata-keys'),
  create: (name: string) => http.post('/odata-keys', { name }),
  revoke: (keyId: Id) => http.delete(`/odata-keys/${keyId}`),
  adminList: () => http.get('/odata-keys/admin/all'),
  adminRevoke: (keyId: Id) => http.delete(`/odata-keys/admin/${keyId}`),
};

// Role Management API methods
const roles = {
  // `tenantId` (global admins only): that organisation's custom roles + the system roles.
  getAll: (params: { tenantId?: Id } = {}) => {
    return http.get('/roles', { params });
  },
  get: (roleId: Id) => {
    return http.get(`/roles/${roleId}`);
  },
  create: (roleData: JsonBody) => {
    return http.post('/roles', roleData);
  },
  update: (roleId: Id, roleData: JsonBody) => {
    return http.put(`/roles/${roleId}`, roleData);
  },
  delete: (roleId: Id) => {
    return http.delete(`/roles/${roleId}`);
  },
  assignPermissions: (roleId: Id, permissionIds: Id[]) => {
    return http.post(`/roles/${roleId}/permissions`, { permissionIds });
  },
  getAllPermissions: () => {
    return http.get('/roles/permissions/all');
  },
};

// AI Assistant API methods
const ai = {
  // Generate SQL + template from natural language prompt
  generate: (prompt: string, outputType = 'email', conversationHistory: unknown[] = []) => {
    return http.post(
      '/ai/generate',
      { prompt, outputType, conversationHistory },
      {
        timeout: 60000, // 60 seconds for AI generation
      }
    );
  },

  // Check if AI assist is configured
  getStatus: () => {
    return http.get('/ai/status');
  },
};

// ============================================
// Geofencing & Real-Time Tracking
// ============================================
const geofences = {
  list: (params?: QueryParams) => http.get('/geofences', { params }),
  get: (id: Id) => http.get(`/geofences/${id}`),
  create: (data: JsonBody) => http.post('/geofences', data),
  update: (id: Id, data: JsonBody) => http.put(`/geofences/${id}`, data),
  delete: (id: Id) => http.delete(`/geofences/${id}`),
};

// Location master data (order-management `/api/locations`) — used by the
// geofencing Site selector. Response shape: { success, count, locations: [...] }
// with snake_case rows (location_id, location_name, ...).
const locations = {
  list: (params?: QueryParams) => http.get('/locations', { params }),
};

const vehicleLocations = {
  getLive: () => http.get('/vehicle-locations/live'),
  getFleet: () => http.get('/vehicle-locations/fleet'),
  getTrail: (regNo: string, params?: QueryParams) =>
    http.get(`/vehicle-locations/${regNo}/trail`, { params }),
  getTrips: (regNo: string, startTime: string, endTime: string) =>
    http.get(`/vehicle-locations/${regNo}/trips`, { params: { startTime, endTime } }),
  // Every vehicle's trips for a window (Trips tab day view, ST-00159); no regNos = all.
  getFleetTrips: (startTime: string, endTime: string, regNos?: readonly string[]) =>
    http.get('/vehicle-locations/trips', {
      params: { startTime, endTime, ...(regNos?.length ? { regNos: regNos.join(',') } : {}) },
    }),
  getLatest: (regNo: string) => http.get(`/vehicle-locations/${regNo}/latest`),
  sendUpdate: (data: JsonBody) => http.post('/vehicle-locations/update', data),
  batchSync: (locations: JsonBody) => http.post('/vehicle-locations/batch', { locations }),
};

const aerialOverlays = {
  // { includeInactive: true } also returns overlays switched off (the Aerials manager);
  // without it only active ones (the map). ST-00228 / TRK-15.
  list: (params?: { includeInactive?: boolean }) => http.get('/aerial-overlays', { params }),
  // 10 min timeout: surveyor orthos are 50–500MB and mine-site uplinks are
  // slow — the axios timeout covers the whole upload, not just the response.
  detectBounds: (formData: FormData, config: AxiosRequestConfig = {}) =>
    http.post('/aerial-overlays/detect-bounds', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
      ...config,
    }),
  save: (data: JsonBody) => http.post('/aerial-overlays', data, { timeout: 60000 }),
  upload: (formData: FormData) =>
    http.post('/aerial-overlays', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    }),
  update: (id: Id, data: JsonBody) => http.put(`/aerial-overlays/${id}`, data),
  delete: (id: Id) => http.delete(`/aerial-overlays/${id}`),
};

// The Geofences page's zone events, cards and time in zones — LexiStar data since ST-00269
// (server/routes/geofenceEvents). `list` params: from, to (YYYY-MM-DD, SAST), type, geofenceId,
// regNo, show (all | alerts | open), limit, offset. `acknowledge` takes a LexiStar event id.
// ST-00282: `acknowledgeAll` takes the filters the list was loaded with, its snapshot and the
// confirmed count (exact: 409 ALERTS_CHANGED otherwise).
const geofenceEvents = {
  list: (params?: QueryParams) => http.get('/geofence-events', { params }),
  summary: () => http.get('/geofence-events/summary'),
  acknowledge: (id: string) => http.put(`/geofence-events/${id}/acknowledge`),
  acknowledgeAll: (data: JsonBody) =>
    http.post('/geofence-events/acknowledge-all', data, { timeout: 120000 }),
  timeInZones: (params?: QueryParams) => http.get('/geofence-events/time-in-zones', { params }),
};

// LexiStar telemetry — GPS device registry (IMEI -> tenant/vehicle)
const telemetry = {
  getDevices: () => http.get('/telemetry/devices'),
  registerDevice: (payload: JsonBody) => http.post('/telemetry/devices', payload),
  updateDevice: (deviceId: Id, payload: JsonBody) =>
    http.put(`/telemetry/devices/${deviceId}`, payload),
  getUnknownDevices: () => http.get('/telemetry/devices/unknown/list'),
};

const productMovement = {
  byGrouping: (params?: QueryParams) =>
    http.get('/product-movement/by-grouping', { params, timeout: 120000 }),
  // Header totals with each ticket counted once across overlapping groupings (ST-00215).
  byGroupingTotals: (params?: QueryParams) =>
    http.get('/product-movement/by-grouping/totals', { params, timeout: 120000 }),
  getProducts: (params?: QueryParams) =>
    http.get('/product-movement/products', { params, timeout: 60000 }),
  getTrend: (params?: QueryParams) =>
    http.get('/product-movement/trend', { params, timeout: 60000 }),
  getProductGroupings: (params?: QueryParams) =>
    http.get('/product-movement/product-groupings', { params, timeout: 60000 }),
};

const notifications = {
  getPreferences: () => http.get('/notifications/preferences'),
  updatePreferences: (preferences: JsonBody) =>
    http.put('/notifications/preferences', { preferences }),
};

// Attach the namespaces to the axios instance itself (same object as before the
// TS conversion: `api.get(...)` and `api.<namespace>.<method>(...)` both work).
const api = Object.assign(http, {
  auth,
  amendmentsV3,
  amendmentApprovals,
  ticketAttachments,
  amendmentAnalytics,
  data,
  signatures,
  logos,
  routeSummary,
  email,
  errorDetection,
  routeIntelligence,
  preloadEvaluation,
  receiptRecon,
  emailReports,
  sharedQueries,
  locationAnalytics,
  vehicleTracker,
  transactionAllocation,
  database,
  customSummaries,
  telegramBI,
  tenants,
  locationOwnership,
  users,
  odataKeys,
  roles,
  ai,
  geofences,
  locations,
  vehicleLocations,
  aerialOverlays,
  geofenceEvents,
  telemetry,
  productMovement,
  notifications,
});

export type Api = typeof api;
export default api;
